import { Alert, Box, Button, Card, CardActions, Tab, Typography } from '@mui/material'
import './MachineConfiguration.css'
import { Link as RouterLink } from 'react-router-dom'
import React, { useMemo, useState } from 'react'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import MachineTab from '../../components/Tabs/MachineTab'
import NetworkTab from '../../components/Tabs/NetworkTab'
import  ApplicationTab  from '../../components/Tabs/ApplicationTab'
import { applyShadowDocument, updateShadowsFromFormState } from '../../services/machines/iotShadow'
import { deviceToFormState } from '../../services/machines/deviceToFormState'
import { useSingleMachineContext } from '../../context/SingleMachineContext'
import AppContext from '../../context/AppContext'
import { ConfigurationFormContext } from '../../context/ConfigurationFormContext'
import { shadowNames, validateChanges } from '../../services/machines/configuration'
import { GlobalChange } from '../../types/formHandling.types'
import { DeviceThing } from '../../types/DeviceThing.types'
import { describeError } from '../../services/machines/errors'

const MachineConfiguration = () => {
    const [currentTab, setCurrentTab] = useState('datasyncd')
    const { awsInstanceIotData } = React.useContext(AppContext)
    const { machine: device } = useSingleMachineContext()
    const [baseline, setBaseline] = useState<DeviceThing>(() => device!)
    const [formState, setFormState] = useState(() => deviceToFormState(device!))
    const [saving, setSaving] = useState(false)
    const [saveErrors, setSaveErrors] = useState<string[]>([])
    const validation = useMemo(() => validateChanges(deviceToFormState(baseline), formState), [baseline, formState])
    const canSave = validation.dirty && !saving && Object.keys(validation.errors).length === 0

    const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => setCurrentTab(newValue)
    // Unreadable fields render no input and all inputs are disabled while saving, so every event here is an editable field.
    const handleInputChange: GlobalChange = (shadow) => (event) => {
        const target = event.target as HTMLInputElement
        const key = target.name.split('.')[1]
        const value = target.type === 'checkbox' ? target.checked : target.value
        setFormState((previous) => ({ ...previous, [shadow]: { ...previous[shadow], [key]: value } }))
    }

    const handleSubmit = async () => {
        if (!canSave || !baseline.thingName || !awsInstanceIotData) return
        setSaving(true)
        setSaveErrors([])
        try {
            const results = await updateShadowsFromFormState(
                baseline.thingName,
                validation.changes,
                baseline.shadowDocuments,
                awsInstanceIotData
            )
            let updated = baseline
            const failures: string[] = []
            for (const shadow of shadowNames) {
                const result = results[shadow]
                if (!result) continue
                if (result.success) updated = applyShadowDocument(updated, shadow, result.document)
                else failures.push(`${shadow}: ${result.message}`)
            }
            const acceptedForm = deviceToFormState(updated)
            setFormState((previous) => {
                const next = { ...previous }
                for (const shadow of shadowNames) {
                    if (results[shadow]?.success) Object.assign(next, { [shadow]: acceptedForm[shadow] })
                }
                return next
            })
            setBaseline(updated)
            setSaveErrors(failures)
            if (failures.length) toast.error('Nie wszystkie zmiany zostały zapisane. Niezapisane pola zachowano — możesz ponowić zapis.')
            else toast.success('Zmiany zapisane pomyślnie')
        } catch (error) {
            setSaveErrors([describeError(error)])
        } finally {
            setSaving(false)
        }
    }

    const form = (
        <Box sx={{ padding: { xs: 1, md: 0 } }}>
            {Object.entries(baseline.shadowErrors).map(([shadow, message]) => (
                <Alert severity="warning" key={shadow} sx={{ mb: 1 }}>
                    Nie można edytować sekcji {shadow}: {message}
                </Alert>
            ))}
            {saveErrors.map((message) => (
                <Alert severity="error" key={message} sx={{ mb: 1 }}>
                    {message}
                </Alert>
            ))}
            {Object.keys(validation.errors).length > 0 && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    Popraw zaznaczone pola przed zapisem.
                </Alert>
            )}
            <Box
                sx={{
                    display: 'flex',
                    marginBottom: { xs: 0, md: 3 },
                    paddingTop: { xs: 1, md: 4 },
                    paddingLeft: { xs: 2, md: 0 },
                }}
            >
                <Typography variant="body2" sx={{ color: '#666', fontSize: 12, textDecoration: 'none' }}>
                    <RouterLink to="/" style={{ textDecoration: 'none', color: '#666' }}>
                        Lista maszyn&nbsp;/&nbsp;
                    </RouterLink>
                </Typography>

                <Typography variant="body2" sx={{ fontSize: 12 }}>
                    Panel konfiguracyjny
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gap: 6,
                    gridTemplateColumns: { xs: '1fr', md: '1fr 3fr' },
                    padding: { xs: 2, md: 0 },
                }}
            >
                <Card sx={{ padding: 4, height: '50%' }} className="card-info" variant="outlined">
                    <Box>
                        <Typography variant="body2" className="text-subheader">
                            Instancja
                        </Typography>
                        <Typography variant="body1">{device?.instance}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" className="text-subheader">
                            Numer seryjny
                        </Typography>
                        <Typography variant="body1">{device?.thingName}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" className="text-subheader">
                            Wersja oprogramowania
                        </Typography>
                        <Typography variant="body1">{device?.version != null && `v${device.version}`}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" className="text-subheader">
                            Ostatnia aktualizacja maszyny
                        </Typography>
                        <Typography variant="body1">{device?.lastUpdateString}</Typography>
                    </Box>
                </Card>
                <Box>
                    <TabContext value={currentTab}>
                        <TabList onChange={handleTabChange}>
                            <Tab label="Maszyna" value="datasyncd" className="card-tab" sx={{ color: '#000' }} />
                            <Tab label="Sieć" value="connman" className="card-tab" sx={{ color: '#000' }} />
                            <Tab label="Aplikacja" value="vend" className="card-tab" sx={{ color: '#000' }} />
                        </TabList>
                        <Card sx={{ padding: '20px 24px 16px 15px', height: '60vh' }} className="card-configuration"
                              variant="outlined">
                            <TabPanel value="datasyncd" sx={{ p: 0, height: '88%' }}>
                                <MachineTab formState={formState} handleChangeByShadow={handleInputChange} />
                            </TabPanel>
                            <TabPanel value="connman" sx={{ p: 0, height: '88%' }}>
                                <NetworkTab formState={formState} handleChangeByShadow={handleInputChange} />
                            </TabPanel>
                            <TabPanel value="vend" sx={{ p: 0, height: '86%' }}>
                                <ApplicationTab formState={formState} handleChangeByShadow={handleInputChange} />
                            </TabPanel>

                            <CardActions
                                sx={{
                                    p: 0,
                                    mt: 2,
                                    display: 'flex',
                                    flexDirection: 'row-reverse',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={!canSave}
                                    color="primary"
                                    sx={{
                                        color: '#fff',
                                        borderRadius: 2,
                                        boxShadow: 'none',
                                        p: '0.4rem 1.7rem',
                                        textTransform: 'none',
                                        fontSize: 16,
                                    }}
                                >
                                    Zapisz zmiany
                                </Button>
                                <ToastContainer
                                    position="bottom-right"
                                    autoClose={4000}
                                    hideProgressBar={false}
                                    newestOnTop={false}
                                    closeOnClick
                                    rtl={false}
                                    pauseOnFocusLoss
                                    draggable
                                    pauseOnHover
                                    theme="colored"
                                />
                            </CardActions>
                        </Card>
                    </TabContext>
                </Box>
            </Box>
        </Box>
    )
    return (
        <ConfigurationFormContext.Provider
            value={{ errors: validation.errors, unavailable: baseline.shadowErrors, fieldReadErrors: baseline.fieldReadErrors, saving }}
        >
            {form}
        </ConfigurationFormContext.Provider>
    )
}

export default MachineConfiguration
